import { useValidateCarrier, NO_PHYSICAL_RETURN_CODE } from "@babackapp/return-widget-components/mutations/use-validate-carrier";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

/**
 * Handles the "Confirm" action on the summary page:
 * 1. Calls POST /public/return-request/validate-carrier with the current draft
 * 2. If the backend says "no-physical-return" → navigate to explanation page (skipping return-mode)
 * 3. Otherwise → navigate to return-mode as usual
 */
export function useSummaryConfirm() {
  const navigate = useNavigate();
  const { orderId } = useWidgetParams();
  const draft = useDraftReturn(orderId);
  const { data: order } = usePublicOrder(orderId);
  const summary = useFinancialSummary(orderId, order ?? null);
  const validateCarrier = useValidateCarrier();
  const [isValidating, setIsValidating] = useState(false);

  const onConfirm = useCallback(async () => {
    if (!draft) return;
    // Exchange-credit goes to shop-now without validate-carrier
    if (summary.exchangeCreditTotal > 0) {
      navigate("../shop-now");
      return;
    }
    setIsValidating(true);
    try {
      const result = await validateCarrier.mutateAsync(draft);
      navigate(result?.code === NO_PHYSICAL_RETURN_CODE ? "../no-physical-return" : "../return-mode");
    } catch {
      navigate("../return-mode");
    } finally {
      setIsValidating(false);
    }
  }, [draft, navigate, validateCarrier, summary.exchangeCreditTotal]);

  return { onConfirm, isValidating };
}
